import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product, Tenant, IntegrationField } from '../../types';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { usePermissions } from '../../hooks/usePermissions';
import { 
  Settings, 
  LinkIcon, 
  KeyRound, 
  Link, 
  Save, 
  Database,
  Check,
  X,
  AlertTriangle,
  ChevronDown,
  List,
  FileText,
  Server,
  Eye,
  EyeOff,
  Copy,
  CheckCheck
} from 'lucide-react';
import ContentLoader from '../ui/ContentLoader';
import { getProductIntegration, createProductIntegration, createLoggerCustomer, updateLoggerCustomer, getLoggerCustomer } from '../../services/api';

interface IntegrateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  tenant: Tenant;
  isIntegrated?: boolean;
  onIntegrationComplete?: () => void;
}

const IntegrateProductModal: React.FC<IntegrateProductModalProps> = ({
  isOpen,
  onClose,
  product,
  tenant,
  isIntegrated = false,
  onIntegrationComplete
}) => {
  const navigate = useNavigate();
  const { canManageIntegrations } = usePermissions();
  const [formValues, setFormValues] = useState<Record<string, string | string[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDropdownField, setOpenDropdownField] = useState<string | null>(null);
  const [visibleSecretFields, setVisibleSecretFields] = useState<Record<string, boolean>>({});
  const [copiedFields, setCopiedFields] = useState<Record<string, boolean>>({});
  const [customOptions, setCustomOptions] = useState<Record<string, string>>({});
  
  // Additional state for logger products
  const [loggerFormData, setLoggerFormData] = useState({
    customer_id: tenant.id,
    target: '',
    status: 'active' as 'active' | 'inactive',
    logger_products: [] as string[],
    logger_type: 'syslog',
    tls_cacert: '',
    tls_mycert: '',
    tls_myprivkey: '',
    tls_permittedpeer: '',
    // Default values for removed fields - these will still be sent to the API
    enable_cyberark: false,
    additional_conditions: ''
  });
  
  const productId = product.id || product._id as string;
  const productType = (product as any).type || 'saas';
  
  // Check permissions
  useEffect(() => {
    if (isOpen && !canManageIntegrations()) {
      onClose();
    }
  }, [isOpen, canManageIntegrations, onClose]);
  
  // Fetch integration data if it's already integrated
  useEffect(() => {
    const fetchIntegrationData = async () => {
      if (isIntegrated && isOpen) {
        setIsLoading(true);
        setError(null);
        
        try {
          if (productType === 'logger') {
            // Fetch logger integration data
            const loggerData = await getLoggerCustomer(tenant.id);
            
            if (loggerData) {
              // Initialize logger form with fetched data
              setLoggerFormData({
                customer_id: tenant.id,
                target: loggerData.target || '',
                status: loggerData.status || 'active',
                logger_products: loggerData.logger_products || [],
                logger_type: loggerData.logger_type || 'syslog',
                tls_cacert: loggerData.tls_config?.cacert || '',
                tls_mycert: loggerData.tls_config?.mycert || '',
                tls_myprivkey: loggerData.tls_config?.myprivkey || '',
                tls_permittedpeer: loggerData.tls_config?.permittedpeer || '',
                enable_cyberark: loggerData.additional_configs?.enableCyberArk || false,
                additional_conditions: loggerData.additional_configs?.conditions || ''
              });
              
              // Add the current product ID to logger_products if not already there
              if (!loggerData.logger_products?.includes(productId)) {
                setLoggerFormData(prev => ({
                  ...prev,
                  logger_products: [...(prev.logger_products || []), productId]
                }));
              }
            }
          } else {
            // Fetch SaaS integration data
            const integrationData = await getProductIntegration(tenant.id, productId);
            
            // Initialize processed values
            const processedValues: Record<string, string | string[]> = {};
            
            // Find multi-select fields
            const multiSelectFields = product.integration_fields?.filter(field => 
              field.type === 'multi-select'
            ) || [];
            
            // Initialize default empty arrays for all multi-select fields
            if (multiSelectFields.length > 0) {
              multiSelectFields.forEach(field => {
                processedValues[field.field_key] = [];
              });
            }
            
            // If we have integration data, process it
            if (integrationData && integrationData.values) {
              // Process each field in the integration data
              Object.entries(integrationData.values).forEach(([key, value]) => {
                const isMultiSelect = multiSelectFields.some(field => field.field_key === key);
                
                if (isMultiSelect) {
                  // Convert string values to arrays if needed
                  if (typeof value === 'string') {
                    processedValues[key] = value.split(',').map(v => v.trim()).filter(Boolean);
                  } else if (Array.isArray(value)) {
                    processedValues[key] = value;
                  }
                  // Note: we already initialized with empty array above
                } else {
                  // Keep non-multi-select values as is
                  processedValues[key] = value as string;
                }
              });
            }
            
            setFormValues(processedValues);
          }
        } catch (error: any) {
          console.error('Error fetching integration data:', error);
          setError('Failed to load integration data');
        } finally {
          setIsLoading(false);
        }
      } else if (!isIntegrated && productType === 'logger') {
        // For new logger integrations, initialize with the product ID
        setLoggerFormData(prev => ({
          ...prev,
          logger_products: [productId]
        }));
      }
    };
    
    fetchIntegrationData();
  }, [isIntegrated, isOpen, tenant.id, productId, product.integration_fields, productType]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownField && !(event.target as Element).closest('.multi-select-container')) {
        setOpenDropdownField(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownField]);
  
  // Handle input change for regular fields
  const handleInputChange = (field: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle input change for logger form fields
  const handleLoggerInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setLoggerFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };
  
  // Handle multi-select dropdown toggle
  const handleDropdownToggle = (fieldKey: string) => {
    setOpenDropdownField(prev => prev === fieldKey ? null : fieldKey);
  };
  
  // Handle option selection in multi-select
  const handleOptionSelect = (fieldKey: string, option: string) => {
    setFormValues(prev => {
      // Ensure currentValues is always an array
      const currentValues = Array.isArray(prev[fieldKey]) 
        ? prev[fieldKey] as string[]
        : [];
      
      if (currentValues.includes(option)) {
        // Remove the option if already selected
        return {
          ...prev,
          [fieldKey]: currentValues.filter((item: string) => item !== option)
        };
      } else {
        // Add the option if not selected
        return {
          ...prev,
          [fieldKey]: [...currentValues, option]
        };
      }
    });
  };
  
  // Remove a selected option
  const handleRemoveOption = (fieldKey: string, option: string) => {
    setFormValues(prev => {
      // Ensure currentValues is always an array
      const currentValues = Array.isArray(prev[fieldKey]) 
        ? prev[fieldKey] as string[]
        : [];
        
      return {
        ...prev,
        [fieldKey]: currentValues.filter((item: string) => item !== option)
      };
    });
  };

  // Add a custom option to multi-select field
  const handleAddCustomOption = (fieldKey: string) => {
    const customOption = customOptions[fieldKey]?.trim();
    if (!customOption) return;

    setFormValues(prev => {
      const currentValues = Array.isArray(prev[fieldKey]) 
        ? prev[fieldKey] as string[]
        : [];
      
      // Add the custom option if it's not already selected
      if (!currentValues.includes(customOption)) {
        return {
          ...prev,
          [fieldKey]: [...currentValues, customOption]
        };
      }
      return prev;
    });

    // Clear the custom option input
    setCustomOptions(prev => ({ ...prev, [fieldKey]: '' }));
  };

  // Handle custom option input change
  const handleCustomOptionChange = (fieldKey: string, value: string) => {
    setCustomOptions(prev => ({ ...prev, [fieldKey]: value }));
  };
  
  // Toggle password visibility
  const togglePasswordVisibility = (fieldKey: string) => {
    setVisibleSecretFields(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Copy field value to clipboard
  const copyToClipboard = (fieldKey: string, value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedFields(prev => ({ ...prev, [fieldKey]: true }));
      
      // Reset copied status after 2 seconds
      setTimeout(() => {
        setCopiedFields(prev => ({ ...prev, [fieldKey]: false }));
      }, 2000);
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (productType === 'logger') {
        // Handle logger product integration
        if (isIntegrated) {
          // Create a properly structured object with all necessary fields
          const updateData = {
            customer_id: tenant.id,
            logger_products: loggerFormData.logger_products,
            status: loggerFormData.status as 'active' | 'inactive',
            logger_type: loggerFormData.logger_type || 'logger01',
            target: loggerFormData.target,
            // Include logger_config
            logger_config: {
              product_type: product.type || 'logger',
              log_type: (product.tags?.logType as string) || 'syslog',
              template_name: 'default_template',
              filename: '%HOSTNAME%.log',
              queue_size: 1000,
              max_disk_space: 100,
              worker_threads: 4
            },
            // Include tls_config
            tls_config: {
              cacert: loggerFormData.tls_cacert,
              mycert: loggerFormData.tls_mycert,
              myprivkey: loggerFormData.tls_myprivkey,
              permittedpeer: loggerFormData.tls_permittedpeer
            }
          };
          
          // Use type assertion to bypass TypeScript checks
          await updateLoggerCustomer(tenant.id, updateData as any);
        } else {
          // Create a properly structured object with all necessary fields
          const createData = {
            customer_id: tenant.id,
            logger_products: loggerFormData.logger_products,
            status: loggerFormData.status as 'active' | 'inactive',
            logger_type: loggerFormData.logger_type || 'logger01',
            target: loggerFormData.target,
            // Include logger_config
            logger_config: {
              product_type: product.type || 'logger',
              log_type: (product.tags?.logType as string) || 'syslog',
              template_name: 'default_template',
              filename: '%HOSTNAME%.log',
              queue_size: 1000,
              max_disk_space: 100,
              worker_threads: 4
            },
            // Include tls_config
            tls_config: {
              cacert: loggerFormData.tls_cacert,
              mycert: loggerFormData.tls_mycert,
              myprivkey: loggerFormData.tls_myprivkey,
              permittedpeer: loggerFormData.tls_permittedpeer
            }
          };
          
          // Use type assertion to bypass TypeScript checks
          await createLoggerCustomer(createData as any);
        }
      } else {
        // Handle SaaS product integration
        // Prepare the integration data object
        const integrationData = {
          customer_id: tenant.id,
          product_id: productId,
          values: formValues
        };
        
        // Debug the data being sent
        // Submitting integration data
        
        // Perform the API call to integrate or update
        await createProductIntegration(integrationData);
      }
      
      setSuccess(isIntegrated ? 'Integration updated successfully' : 'Product integrated successfully');
      
      // Call the callback if provided
      if (onIntegrationComplete) {
        onIntegrationComplete();
      }
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error('Error saving integration:', error);
      setError('Failed to save integration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Get title for the modal
  const getModalTitle = () => {
    const actionText = isIntegrated ? 'Update' : 'Setup';
    const productTypeText = productType === 'logger' ? 'Logger' : 'SaaS';
    return `${actionText} ${product.name} ${productTypeText} Integration`;
  };
  
  // Render appropriate icon for field type
  const renderFieldIcon = (type: string) => {
    switch (type) {
      case 'secret':
        return <KeyRound size={16} className="text-gray-400" />;
      case 'url':
        return <Link size={16} className="text-gray-400" />;
      case 'number':
        return <Database size={16} className="text-gray-400" />;
      case 'multi-select':
        return <List size={16} className="text-gray-400" />;
      default:
        return <Settings size={16} className="text-gray-400" />;
    }
  };
  
  // Render the multi-select dropdown
  const renderMultiSelect = (field: IntegrationField) => {
    // Ensure options exist
    const options = field.options && Array.isArray(field.options) ? field.options : [];
    
    // Check if we have values for this field
    const selectedValues = Array.isArray(formValues[field.field_key]) 
      ? formValues[field.field_key] as string[]
      : [];
    
    return (
      <div className="multi-select-container relative">
        {/* Selected values display area */}
        <div 
          className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2 min-h-10 cursor-pointer focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          onClick={() => handleDropdownToggle(field.field_key)}
        >
          <div className="flex flex-wrap gap-2 items-center">
            {selectedValues.length === 0 ? (
              <span className="text-gray-400 text-sm">
                Select {field.label.toLowerCase()} or add custom options...
              </span>
            ) : (
              <>
                {selectedValues.map((value: string) => (
                  <div key={value} className="bg-primary-700/30 text-primary-300 px-2 py-0.5 rounded text-xs flex items-center">
                    <span>{value}</span>
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveOption(field.field_key, value);
                      }}
                      className="ml-1 text-primary-300/70 hover:text-primary-300"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </>
            )}
            <div className="ml-auto">
              <ChevronDown 
                size={16} 
                className={`transition-transform duration-200 ${
                  openDropdownField === field.field_key ? 'rotate-180' : ''
                }`} 
              />
            </div>
          </div>
        </div>
        
        {/* Dropdown options */}
        {openDropdownField === field.field_key && (
          <div className="absolute z-50 mt-1 w-full bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {/* Custom option input */}
            <div className="p-3 border-b border-gray-600">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Add custom option..."
                  value={customOptions[field.field_key] || ''}
                  onChange={(e) => handleCustomOptionChange(field.field_key, e.target.value)}
                  className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white placeholder-gray-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomOption(field.field_key);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleAddCustomOption(field.field_key)}
                  className="px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
            
            {/* Predefined options */}
            {options.length > 0 ? (
              options.map((option: string) => (
                <div
                  key={option}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-700 ${
                    selectedValues.includes(option) 
                      ? 'bg-primary-700/50 text-primary-200' 
                      : 'text-white'
                  }`}
                  onClick={() => {
                    handleOptionSelect(field.field_key, option);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span>{option}</span>
                    {selectedValues.includes(option) && (
                      <Check size={16} className="text-primary-300" />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-300 text-center">
                No predefined options available
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  // Render logger product integration form
  const renderLoggerForm = () => {
    return (
      <div className="space-y-6">
        {/* Customer Information Section */}
        <div className="bg-background-card border border-card-border rounded-lg overflow-hidden">
          <div className="bg-slate-800/30 px-4 py-3 font-medium text-gray-300 flex items-center">
            <Server size={16} className="mr-2 text-primary" />
            Customer Information
          </div>
          <div className="p-5 space-y-4">
            <div className="form-group">
              <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                Customer
              </label>
              <div className="bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm flex justify-between items-center">
                <span>{tenant.name}</span>
                <button
                  type="button"
                  onClick={() => copyToClipboard('customer', tenant.name)}
                  className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                  title="Copy to clipboard"
                >
                  {copiedFields['customer'] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">This customer is pre-selected based on the integration context.</p>
            </div>

            <div className="form-group">
              <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                Target <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="target"
                  value={loggerFormData.target}
                  onChange={handleLoggerInputChange}
                  className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                  placeholder="Enter target IP or hostname"
                  required
                />
                {loggerFormData.target && (
                  <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                    <button
                      type="button"
                      onClick={() => copyToClipboard('target', loggerFormData.target)}
                      className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedFields['target'] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Logger Type <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    name="logger_type"
                    value={loggerFormData.logger_type}
                    onChange={handleLoggerInputChange}
                    className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                  >
                    <option value="syslog">Syslog</option>
                    <option value="json">JSON</option>
                    <option value="cef">CEF</option>
                  </select>
                  {loggerFormData.logger_type && (
                    <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                      <button
                        type="button"
                        onClick={() => copyToClipboard('logger_type', loggerFormData.logger_type)}
                        className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedFields['logger_type'] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Status <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    name="status"
                    value={loggerFormData.status}
                    onChange={handleLoggerInputChange}
                    className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {loggerFormData.status && (
                    <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                      <button
                        type="button"
                        onClick={() => copyToClipboard('status', loggerFormData.status)}
                        className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedFields['status'] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TLS Certificate Configuration Section */}
        <div className="bg-background-card border border-card-border rounded-lg overflow-hidden">
          <div className="bg-slate-800/30 px-4 py-3 font-medium text-gray-300 flex items-center">
            <FileText size={16} className="mr-2 text-primary" />
            TLS Certificate Configuration
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  CA Certificate Path
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="tls_cacert"
                    value={loggerFormData.tls_cacert}
                    onChange={handleLoggerInputChange}
                    className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                    placeholder="/etc/ssl/rsyslog/CA.pem"
                  />
                  {loggerFormData.tls_cacert && (
                    <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                      <button
                        type="button"
                        onClick={() => copyToClipboard('tls_cacert', loggerFormData.tls_cacert)}
                        className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedFields['tls_cacert'] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">Path to the CA certificate file on the client machine</p>
              </div>

              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Client Certificate Path
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="tls_mycert"
                    value={loggerFormData.tls_mycert}
                    onChange={handleLoggerInputChange}
                    className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                    placeholder="/etc/ssl/rsyslog/client-cert.pem"
                  />
                  {loggerFormData.tls_mycert && (
                    <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                      <button
                        type="button"
                        onClick={() => copyToClipboard('tls_mycert', loggerFormData.tls_mycert)}
                        className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedFields['tls_mycert'] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Client Private Key Path
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="tls_myprivkey"
                    value={loggerFormData.tls_myprivkey}
                    onChange={handleLoggerInputChange}
                    className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                    placeholder="/etc/ssl/rsyslog/client-key.pem"
                  />
                  {loggerFormData.tls_myprivkey && (
                    <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                      <button
                        type="button"
                        onClick={() => copyToClipboard('tls_myprivkey', loggerFormData.tls_myprivkey)}
                        className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedFields['tls_myprivkey'] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Permitted Peer
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="tls_permittedpeer"
                    value={loggerFormData.tls_permittedpeer}
                    onChange={handleLoggerInputChange}
                    className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                    placeholder="*.example.com"
                  />
                  {loggerFormData.tls_permittedpeer && (
                    <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                      <button
                        type="button"
                        onClick={() => copyToClipboard('tls_permittedpeer', loggerFormData.tls_permittedpeer)}
                        className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedFields['tls_permittedpeer'] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      size="lg"
    >
      <div className="relative">
        {isLoading ? (
          <ContentLoader
            message="Loading integration data..."
            size="lg"
            withCard={false}
            minHeight="300px"
          />
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Success message */}
            {success && (
              <div className="mb-4 p-3 bg-green-900/30 border border-green-800 rounded-lg flex items-center">
                <Check size={18} className="text-green-400 mr-2" />
                <p className="text-green-400 text-sm">{success}</p>
              </div>
            )}
            
            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg flex items-center">
                <AlertTriangle size={18} className="text-red-400 mr-2" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
            
            {/* Product info */}
            <div className="mb-6 bg-background-card border border-card-border rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-background-darker rounded-md flex items-center justify-center mr-4 border border-card-border">
                  {product.logo_url ? (
                    <img 
                      src={product.logo_url} 
                      alt={product.name} 
                      className="max-w-full max-h-full object-contain" 
                    />
                  ) : (
                    <Settings className="h-6 w-6 text-gray-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-lg">{product.name}</h3>
                  <p className="text-sm text-gray-400">
                    {`Configure ${productType === 'logger' ? 'Logger' : 'SaaS'} integration for ${tenant.name}`}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Integration fields based on product type */}
            {productType === 'logger' ? (
              renderLoggerForm()
            ) : (
              <div className="space-y-4">
                {product.integration_fields && product.integration_fields.length > 0 ? (
                  product.integration_fields
                    .filter(field => field.type !== 'soar-mapping') // Filter out soar-mapping fields
                    .map((field: IntegrationField) => (
                    <div key={field.field_key} className="form-group">
                      <label className="flex items-center text-sm font-medium text-gray-300 mb-1.5">
                        {renderFieldIcon(field.type)}
                        <span className="ml-1.5">{field.label}</span>
                        {field.is_required && <span className="text-red-400 ml-1">*</span>}
                      </label>
                      
                      {field.type === 'multi-select' ? (
                        renderMultiSelect(field)
                      ) : (
                        <div className="relative">
                          <input
                            type={field.type === 'secret' && !visibleSecretFields[field.field_key] ? 'password' : 'text'}
                            value={formValues[field.field_key]?.toString() || ''}
                            onChange={(e) => handleInputChange(field.field_key, e.target.value)}
                            className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-16"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            required={field.is_required}
                          />
                          <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                            {field.type === 'secret' && (
                              <button
                                type="button"
                                onClick={() => togglePasswordVisibility(field.field_key)}
                                className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                                title={visibleSecretFields[field.field_key] ? "Hide value" : "Show value"}
                              >
                                {visibleSecretFields[field.field_key] ? <EyeOff size={16} /> : <Eye size={16} />}
                              </button>
                            )}
                            {formValues[field.field_key] && (
                              <button
                                type="button"
                                onClick={() => copyToClipboard(field.field_key, formValues[field.field_key]?.toString() || '')}
                                className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                                title="Copy to clipboard"
                              >
                                {copiedFields[field.field_key] ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {field.description && (
                        <p className="text-xs text-gray-400 mt-1">{field.description}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <p>No configuration fields required for this integration.</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex justify-end mt-6 pt-4 border-t border-card-border">
              <Button
                variant="outline"
                type="button"
                onClick={onClose}
                className="mr-3"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                type="submit"
                isLoading={isSaving}
                disabled={isSaving}
                className="bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
              >
                {isIntegrated ? 'Update' : 'Save'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default IntegrateProductModal; 